export const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function getFocusableElements(container: HTMLElement) {
  const elements = Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  ).filter(el => !el.hasAttribute('disabled') && el.tabIndex !== -1);

  // Match browser tab order: positive tabindex first, then tabindex 0 in DOM order.
  return elements.sort((a, b) => {
    const aTab = a.tabIndex > 0 ? a.tabIndex : 0;
    const bTab = b.tabIndex > 0 ? b.tabIndex : 0;

    if (aTab !== bTab) {
      if (aTab === 0) return 1;
      if (bTab === 0) return -1;
      return aTab - bTab;
    }

    if (a === b) return 0;
    const position = a.compareDocumentPosition(b);
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return 0;
  });
}

/** Map document.activeElement to a focusable inside the container. */
export function resolveActiveFocusable(
  container: HTMLElement,
  focusables: HTMLElement[]
) {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement)) return null;
  if (focusables.includes(active)) return active;

  let el: HTMLElement | null = active;
  while (el && container.contains(el)) {
    if (focusables.includes(el)) return el;
    el = el.parentElement;
  }

  return null;
}

function getRadioGroupFocusables(activeEl: HTMLElement) {
  if (activeEl.getAttribute('role') !== 'radio') return null;

  const group = activeEl.closest('fieldset');
  if (!group) return null;

  const radios = getFocusableElements(group as HTMLElement).filter(
    el => el.getAttribute('role') === 'radio'
  );

  return radios.length > 1 ? radios : null;
}

/** Focusables to move between with arrow keys from the current element. */
export function getArrowFocusables(
  container: HTMLElement,
  activeEl: HTMLElement
) {
  return getRadioGroupFocusables(activeEl) ?? getFocusableElements(container);
}
